import { IconComponentProps } from '@nexelem/icons';
import { FC, ForwardedRef, ReactNode, forwardRef, isValidElement } from 'react';
import { Button, ButtonProps, LinkComponentProps } from '../Button';
import { VariantProps, cva } from 'class-variance-authority';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/pro-light-svg-icons';

const iconButtonVariants = cva(
  'shrink-0 rounded-full p-0 disabled:[&_svg]:text-text-disabled! disabled:[&_svg]:opacity-40!',
  {
    variants: {
      size: {
        extraSmall: 'h-6 w-6 [&_svg]:size-3!',
        small: 'h-11 w-11 [&_svg]:size-5!',
        medium: 'h-14 w-14 [&_svg]:size-5!',
        large: 'h-12 w-12 [&_svg]:size-7!',
      },
    },
    defaultVariants: {
      size: 'small',
    },
  },
);

type IconVariantProps = VariantProps<typeof iconButtonVariants>;

export type IconButtonProps = Omit<ButtonProps, 'StartIcon' | 'EndIcon' | 'size'> &
  LinkComponentProps &
  IconVariantProps & {
    Icon: ReactNode | FC<IconComponentProps>;
    iconProps?: IconComponentProps['iconProps'];
  };

export const IconButton = forwardRef(
  (
    { Icon, className, iconProps, size, isLoading, disabled, component, to, target, state, ...props }: IconButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => {
    const buttonProps = component
      ? { component, to, target, state, ...props }
      : { disabled: disabled || isLoading, ...props };

    return (
      <Button
        {...buttonProps}
        className={iconButtonVariants({ size, className })}
        ref={ref}
        variant="text"
        StartIcon={
          isLoading ? (
            <FontAwesomeIcon className="animate-spin" icon={faRotate} />
          ) : isReactNode(Icon) ? (
            Icon
          ) : (
            <Icon iconProps={iconProps} />
          )
        }
      />
    );
  },
);

IconButton.displayName = 'IconButton';

const isReactNode = (node: ReactNode | FC<IconComponentProps>): node is ReactNode => isValidElement(node);
